import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { ShoppingBag, X, Send, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { key: "fans", label: "Fans", subtitle: "Hand fans — large festival edition" },
  { key: "stickers", label: "Stickers", subtitle: "High-quality vinyl" },
  { key: "ashtrays", label: "Ashtrays", subtitle: "Hand-crafted pieces" },
];

export default function Merch() {
  const kicker = useContent("page.merch.kicker", "/ 13 — MERCH");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({ fans: true, stickers: false, ashtrays: false });
  const [inquiryFor, setInquiryFor] = useState(null);
  const title = useContent("page.merch.title", "MERCH");
  const intro = useContent(
    "page.merch.intro",
    "Limited pieces designed alongside the Inner Pulse / Soundation aesthetic. Send an inquiry — we'll confirm availability, shipping to your country and total price before any payment."
  );

  useEffect(() => {
    api
      .get("/merch")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((p) => {
      if (!p.available) return;
      g[p.category] = g[p.category] || [];
      g[p.category].push(p);
    });
    return g;
  }, [items]);

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div data-testid="merch-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="merch-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-2xl mt-8 leading-relaxed">
            {intro}
          </p>
        </div>

        {loading && (
          <p className="font-body text-silver-500 text-sm" data-testid="merch-loading">
            Loading collections…
          </p>
        )}

        <div className="flex flex-col gap-4" data-testid="merch-categories">
          {CATEGORIES.map((cat) => {
            const products = grouped[cat.key] || [];
            const isOpen = open[cat.key];
            return (
              <section
                key={cat.key}
                className="hairline"
                data-testid={`merch-category-${cat.key}`}
              >
                <button
                  onClick={() => toggle(cat.key)}
                  className="w-full flex items-center justify-between px-6 md:px-8 py-6 md:py-8 hover:bg-silver-950/40 transition-colors group"
                  data-testid={`merch-toggle-${cat.key}`}
                >
                  <div className="flex items-center gap-6">
                    <span className="font-title text-[0.62rem] tracking-[0.4em] text-silver-600">
                      / 0{CATEGORIES.indexOf(cat) + 1}
                    </span>
                    <div className="text-left">
                      <div className="font-title text-2xl md:text-4xl tracking-[0.1em] text-silver-100 group-hover:text-silver-glow transition-all uppercase">
                        {cat.label}
                      </div>
                      <div className="font-body text-xs md:text-sm text-silver-500 mt-1">
                        {cat.subtitle} · {products.length} {products.length === 1 ? "piece" : "pieces"}
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    size={22}
                    className={`text-silver-400 transition-transform duration-500 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isOpen && (
                  <div
                    className="hairline-t px-6 md:px-8 py-10"
                    data-testid={`merch-list-${cat.key}`}
                  >
                    {products.length === 0 && (
                      <p className="font-body text-silver-500 text-sm text-center py-8">
                        New pieces coming soon.
                      </p>
                    )}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {products.map((p) => (
                        <ProductCard
                          key={p.id}
                          p={p}
                          onInquire={() => setInquiryFor(p)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <p className="font-body text-silver-600 text-xs mt-12 max-w-2xl">
          All inquiries are confirmed manually before payment — shipping is calculated based on your country.
          Reply within 24-48h.
        </p>
      </div>

      {inquiryFor && (
        <InquiryModal product={inquiryFor} onClose={() => setInquiryFor(null)} />
      )}
    </div>
  );
}

function ProductCard({ p, onInquire }) {
  return (
    <article
      className="flex flex-col gap-4 group"
      data-testid={`merch-product-${p.id}`}
    >
      <div className="aspect-square bg-silver-900/40 hairline overflow-hidden relative">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.title}
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            style={{ filter: "contrast(1.05)" }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={36} className="text-silver-700" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="font-title text-base md:text-lg tracking-[0.1em] text-silver-100 leading-snug">
          {p.title}
        </h3>
        {p.details && (
          <div className="font-title text-[0.55rem] tracking-[0.26em] text-silver-500 uppercase">
            {p.details}
          </div>
        )}
        {p.description && (
          <p className="font-body text-silver-400 text-xs leading-relaxed">
            {p.description}
          </p>
        )}
        <div className="flex items-center justify-between mt-2">
          {p.price && (
            <span className="font-title text-sm tracking-[0.1em] text-silver-100">
              {p.price}
            </span>
          )}
          <button
            onClick={onInquire}
            className="btn-sharp ml-auto"
            data-testid={`merch-inquire-${p.id}`}
          >
            <Send size={12} /> Request to Buy
          </button>
        </div>
      </div>
    </article>
  );
}

function InquiryModal({ product, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country: "",
    quantity: "1",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.country) {
      toast.error("Name, email and country are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/merch/inquiry", {
        ...form,
        product_id: product.id,
        product_title: product.title,
      });
      setSent(true);
      toast.success("Request sent — we'll reply within 48h.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
      data-testid="merch-inquiry-modal"
    >
      <div
        className="bg-black hairline w-full max-w-2xl p-8 md:p-10 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-silver-400 hover:text-silver-100"
          aria-label="Close"
          data-testid="merch-modal-close"
        >
          <X size={20} />
        </button>

        <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500 uppercase mb-2">
          / Request to Buy
        </div>
        <h2 className="font-title text-2xl md:text-3xl tracking-[0.1em] text-silver-100 mb-2">
          {product.title}
        </h2>
        <p className="font-body text-silver-500 text-xs mb-8">
          We'll reply with availability + shipping cost to your country before any payment.
        </p>

        {sent ? (
          <div className="flex flex-col gap-4 items-start py-4" data-testid="merch-inquiry-success">
            <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-300">
              / REQUEST DELIVERED
            </div>
            <p className="font-body text-silver-300 text-sm max-w-md">
              Thank you. We'll review your request and reply within 24-48h with shipping cost + payment details.
            </p>
            <button onClick={onClose} className="btn-ghost mt-2" data-testid="merch-inquiry-close-after">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-5" data-testid="merch-inquiry-form">
            <div className="grid md:grid-cols-2 gap-5">
              <label>
                <span className="ip-label">Name *</span>
                <input
                  className="ip-input"
                  value={form.name}
                  onChange={update("name")}
                  required
                  data-testid="merch-inquiry-name"
                />
              </label>
              <label>
                <span className="ip-label">Email *</span>
                <input
                  type="email"
                  className="ip-input"
                  value={form.email}
                  onChange={update("email")}
                  required
                  data-testid="merch-inquiry-email"
                />
              </label>
              <label>
                <span className="ip-label">Country *</span>
                <input
                  className="ip-input"
                  value={form.country}
                  onChange={update("country")}
                  placeholder="For shipping cost"
                  required
                  data-testid="merch-inquiry-country"
                />
              </label>
              <label>
                <span className="ip-label">Quantity</span>
                <input
                  type="number"
                  min="1"
                  className="ip-input"
                  value={form.quantity}
                  onChange={update("quantity")}
                  data-testid="merch-inquiry-quantity"
                />
              </label>
            </div>
            <label>
              <span className="ip-label">Note (optional)</span>
              <textarea
                rows={3}
                className="ip-input resize-none"
                value={form.message}
                onChange={update("message")}
                placeholder="Any preference, message or question."
                data-testid="merch-inquiry-message"
              />
            </label>
            <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
              <p className="font-body text-silver-600 text-xs max-w-xs">
                No payment yet. You'll receive an email reply with shipping cost.
              </p>
              <button
                type="submit"
                className="btn-sharp"
                disabled={loading}
                data-testid="merch-inquiry-submit"
              >
                <Send size={12} /> {loading ? "Sending…" : "Send Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
